import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    EntityValidations,
    ISpecificEntityFormProps,
    IApp03Epic,
    SaveStatus,
    App03Epic,
    CustomField,
    App03EpicObserver,
    IUser,
    App03EpicDep
} from '../../types';
import { DataContext } from '../DataContext';
import { CrTextField } from '../cr/CrTextField';
import { CrDatePicker } from '../cr/CrDatePicker';
import { CrDropdown } from '../cr/CrDropdown';
import { CrCommandBar } from '../cr/CrCommandBar';
import { EntityForm } from '../EntityForm';
import { DateService, LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import useMobileDetector from '../useMobileDetector';
import { App03EpicStatus } from '../../types/App03Epic';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';
import { OrbUserContext } from '../OrbUserContext';
import EpicEvidenceList from './EV/EpicEvidenceList';
import { TaskPriority } from '../../refData/WorkflowType';
import { CrUserPicker } from '../cr/CrUserPicker';
import { App03Frameworks } from '../../types/App03Workflow';

class EpicFormValidations extends EntityValidations {
    public StartDate: string = null;
    public EndDate: string = null;
    public EpicStatus: string = null;
}

interface IEpicFormProps extends ISpecificEntityFormProps {
    workflowID: number;
    frameworkID: App03Frameworks;
}

export const EpicFormPage = (props: IEpicFormProps): React.ReactElement => {
    const { dataServices, lookupData, loadLookupData: { customFieldDfns } } = useContext(DataContext);
    const { userContext, userPermissions } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;
    const isMobile = useMobileDetector();
    const [evidence_ListFilterText, setEvidence_ListFilterText] = useState<string>('');

    useMemo(() => customFieldDfns(), [customFieldDfns]);
    const [workflowUsers, setWorkflowUsers] = useState<IUser[]>([]);
    const [workflowEpics, setWorkflowEpics] = useState<IApp03Epic[]>([]);

    useEffect(() => {
        const fetchWorkflowUsers = async () => {
            try {
                const users = (await dataServices.app03WorkflowUserService.readWorkflowUsers(props.workflowID)).map(u => u.User);
                setWorkflowUsers(users);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        const fetchWorkflowEpics = async () => {
            try {
                const epics = (await dataServices.app03EpicService.readAllForAddTaskDropdown(props.workflowID));

                // Exclude current epic (so it can't be selected as a dependency)
                const filteredEpics = props.entityId > 0
                    ? epics.filter(e => e.ID !== props.entityId)
                    : epics;

                setWorkflowEpics(filteredEpics);

            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchWorkflowUsers();
        fetchWorkflowEpics();

    }, []);

    const validateEntity = (epic: IApp03Epic): Promise<EpicFormValidations> => {
        const errors = new EpicFormValidations();

        // Title required
        if (!epic.Title || epic.Title.trim() === '') {
            errors.Title = 'Title is required';
            errors.Valid = false;
        } else {
            errors.Title = null;
        }

        // Start Date required
        if (!epic.StartDate) {
            errors.StartDate = 'Start Date is required';
            errors.Valid = false;
        } else {
            errors.StartDate = null;
        }

        // Target End Date required
        if (!epic.TargetEndDate) {
            errors.EndDate = 'Target End Date is required';
            errors.Valid = false;
        } else {
            errors.EndDate = null;
        }

        // End date cannot be earlier than start date
        if (
            epic.StartDate &&
            epic.TargetEndDate &&
            epic.TargetEndDate < epic.StartDate
        ) {
            errors.EndDate = 'End date cannot be earlier than start date';
            errors.Valid = false;
        }

        // Epic Status required
        if (epic.EpicStatus == null) {
            errors.EpicStatus = 'Epic Status is required';
            errors.Valid = false;
        } else {
            errors.EpicStatus = null;
        }

        return Promise.resolve(errors);
    };

    const filteredCustomFieldDfns = useMemo(() => {
        const appPrefix = props.frameworkID === App03Frameworks.Scrum ? "Agile Sprints - " : "Group Workstreams - ";
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                `${appPrefix}Epics`,
            ]
        );
    }, [lookupData.CustomFieldDfns]);


    const handleEvidence_ChangeFilterText = (value: string): void => {
        setEvidence_ListFilterText(value);
    }

    return (
        <div className={`${styles.cr} ${styles.updatesContainer}`} style={{ width: '100%' }}>
            <EntityForm<IApp03Epic, EpicFormValidations>
                {...props}
                entityName="Epic"
                mode="page"
                renderFormFields={(changeHandlers, formState, saveEntity) => {
                    const { FormData: formData, ValidationErrors: errors, FormIsDirty, FormSaveStatus } = formState;

                    const commandBarItems = FormSaveStatus === SaveStatus.Pending
                        ? [{
                            key: 'Save',
                            text: 'Saving...',
                            iconProps: { iconName: 'Sync' },
                            disabled: true,
                        }]
                        : !FormIsDirty
                            ? [{
                                key: 'Close',
                                text: 'Close',
                                iconProps: { iconName: 'Cancel' },
                                onClick: () => props.onCancelled(),
                            }]
                            : [
                                {
                                    key: 'Save',
                                    text: 'Save',
                                    iconProps: { iconName: 'Save' },
                                    onClick: () => saveEntity(),
                                },
                                {
                                    key: 'Cancel',
                                    text: 'Cancel',
                                    iconProps: { iconName: 'Cancel' },
                                    onClick: () => props.onCancelled(true),
                                }
                            ];

                    const startLabel = (
                        <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>Start Date</span></div>
                                <div className={styles.sectionQuestionCol2}></div>
                            </div>
                        </div>
                    );

                    const endLabel = (
                        <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>Target End Date</span></div>
                                <div className={styles.sectionQuestionCol2}></div>
                            </div>
                        </div>
                    );

                    const statusLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Status</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const priotityLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Priority</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const epicOwnerLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Epic Owner</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqCostCentreValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const epicPanelLabel = <div className='two-fields-sidebyside-lbl'>
                        <div className={styles.flexContainerSectionQuestion}>
                            <div className={styles.sectionQuestionCol1}><span>Epic Panel</span></div>
                            <div className={styles.sectionQuestionCol2}>
                                {/* <img src={reqDirectorateIdValidationImg} /> */}
                            </div>
                        </div>
                    </div>;

                    const taskPriorityArray = Object.keys(TaskPriority)
                        .filter(key => !isNaN(Number(TaskPriority[key])))
                        .map(key => ({ ID: TaskPriority[key], Title: key }));


                    return (
                        <div>
                            <CrCommandBar items={commandBarItems} />

                            {/* Epic Details Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '10px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Epic Details</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* Title */}
                                    <div className={styles.formField}>
                                        <div className={styles.flexContainerSectionQuestion}>
                                            <div className={styles.sectionQuestionCol1}><span>Title</span></div>
                                        </div>
                                        <CrTextField
                                            maxLength={200}
                                            value={formData.Title}
                                            onChange={(ev, val) => changeHandlers.changeTextField(val, 'Title')}
                                            errorMessage={errors.Title}
                                            required
                                        />
                                    </div>

                                    {/* Details */}
                                    <div className={styles.formField}>
                                        <div className={styles.flexContainerSectionQuestion}>
                                            <div className={styles.sectionQuestionCol1}><span>Details</span></div>
                                        </div>
                                        <CrTextField
                                            multiline
                                            rows={5}
                                            maxLength={2000}
                                            value={formData.Description}
                                            onChange={(ev, val) => changeHandlers.changeTextField(val, 'Description')}
                                        />
                                    </div>

                                    {/* Start and End Dates */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && (
                                            <div className='two-fields-sidebyside-r1'>
                                                {startLabel}
                                                {endLabel}
                                            </div>
                                        )}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && startLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.StartDate)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'StartDate')}
                                                    required={true}
                                                    errorMessage={errors.StartDate}
                                                />
                                            </div>
                                            {isMobile && endLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.TargetEndDate)}
                                                    minDate={DateService.removeTimezoneOffset(formData.StartDate)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'TargetEndDate')}
                                                    required={true}
                                                    errorMessage={errors.EndDate}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status and Priority */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && (
                                            <div className='two-fields-sidebyside-r1'>
                                                {statusLabel}
                                                {priotityLabel}
                                            </div>
                                        )}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && statusLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={[
                                                        { key: App03EpicStatus.NotStarted, text: "Not Started" },
                                                        { key: App03EpicStatus.InProgress, text: "In Progress" },
                                                        { key: App03EpicStatus.Completed, text: "Completed" },
                                                        { key: App03EpicStatus.Canceled, text: "Canceled" },
                                                    ]}
                                                    selectedKey={formData.EpicStatus}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'EpicStatus')}
                                                    required={true}
                                                    errorMessage={errors.EpicStatus}
                                                />
                                            </div>
                                            {isMobile && priotityLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(taskPriorityArray)}
                                                    selectedKey={formData.EpicPriorityID}
                                                    onChange={(_, o) => changeHandlers.changeDropdown(o, 'EpicPriorityID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 5th row */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && <div className='two-fields-sidebyside-r1'>
                                            {epicOwnerLabel}
                                            {epicPanelLabel}
                                        </div>}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && epicOwnerLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrUserPicker
                                                    users={workflowUsers}
                                                    selectedUsers={formData.EpicOwnerID && [formData.EpicOwnerID]}
                                                    onChange={v => changeHandlers.changeUserPicker(v, 'EpicOwnerID')}
                                                />
                                            </div>
                                            {isMobile && epicPanelLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrUserPicker
                                                    users={workflowUsers}
                                                    itemLimit={10}
                                                    selectedUsers={formData.App03EpicObservers?.map(u => u.UserID)}
                                                    onChange={v => changeHandlers.changeMultiUserPicker(v, 'App03EpicObservers', new App03EpicObserver(), 'UserID')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 6th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Dependent On</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    multiSelect
                                                    options={LookupService.entitiesToSelectableOptions(workflowEpics)}
                                                    selectedKeys={formData.Dependencies?.map(x => x.DependentEpicID)}
                                                    onChange={(_, v) => changeHandlers.changeMultiDropdown(v, 'Dependencies', new App03EpicDep(), 'DependentEpicID')}
                                                />
                                            </div>
                                        </div>
                                    </div>



                                </div>
                            </div>

                            {/* Additional Information Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Additional Information</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Additional Information</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrCustomFields
                                                    customFieldDfns={filteredCustomFieldDfns}
                                                    selectedItems={LookupService.customFieldsToDropdownWithText(formData.CustomFields)}
                                                    onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                        </div>
                    );
                }}
                loadEntity={id => dataServices.app03EpicService.read(id, true, true)}
                loadNewEntity={() => new App03Epic()}
                loadEntityValidations={() => new EpicFormValidations()}
                onValidateEntity={validateEntity}
                onCreate={entity => dataServices.app03EpicService.create(entity)}
                onUpdate={entity => dataServices.app03EpicService.update(entity.ID, entity)}
                parentEntities={dataServices.app03EpicService.parentEntities}
                childEntities={[
                    {
                        ObjectParentProperty: 'CustomFields',
                        ParentIdProperty: 'App03EpicID',
                        ChildIdProperty: 'CustomFieldDefnID',
                        ChildService: dataServices.customFieldService
                    },
                    {
                        ObjectParentProperty: 'App03EpicObservers',
                        ParentIdProperty: 'EpicID',
                        ChildIdProperty: 'UserID',
                        ChildService: dataServices.app03EpicObserverService
                    },
                    {
                        ObjectParentProperty: 'Dependencies',
                        ParentIdProperty: 'EpicID',
                        ChildIdProperty: 'DependentEpicID',
                        ChildService: dataServices.app03EpicDepService
                    }
                ]}
            />

            {props.entityId > 0 &&
                <>
                    <div>
                        <div style={{ marginBottom: '20px', marginTop: '50px' }}>
                            <div style={{ display: 'flex' }}>
                                <div className={styles.sectionATitle}>Epic Discussion, General Comments and File Attachments</div>
                            </div>
                        </div>
                    </div>
                    <div>

                        <div style={{ minHeight: '120px' }}>
                            <EpicEvidenceList
                                frameworkID={props.frameworkID}
                                epicID={props.entityId}
                                currentUserId={currentUserId}
                                superUserPermission={userPermissions.UserIsSystemAdmin()}
                                isViewOnly={false}
                                filterText={evidence_ListFilterText}
                                onChangeFilterText={handleEvidence_ChangeFilterText}
                                evChangesCounter={1}
                                {...props}
                                errorHandling={props.errorHandling}
                            />
                        </div>

                    </div>
                </>
            }

        </div>
    );
};
